import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { ProjectChecklistCenterModal } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistCenterModal';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  getFirstLine,
  isMultiLine,
} from '@components/TaskList/TaskListItem.web';
import { EnhancedAnimatedCheckbox } from '@components/Web/AnimatedCheckbox';
import { ProjectChecklist } from '@graphql/generated';
import { useOutsideClickAndEscape } from '@hooks/useOutsideClick.web';
import theme from '@themes/theme';

type ProjectChecklistItemProps = {
  item: ProjectChecklist;
  width?: number;
  onCompletedProjectChecklistItem: (
    item: ProjectChecklist,
    callback?: () => void
  ) => void;
  onSaveProjectChecklistItem: (name: string, item?: ProjectChecklist) => void;
  onDeleteProjectChecklistItem: (itemId: string) => void;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  selectedItemId?: string;
  setSelectedItemId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  canEdit: boolean;
  marginBottom?: keyof typeof theme.spacing;
  isDragActive?: boolean;
};

export const ProjectChecklistItem: React.FC<ProjectChecklistItemProps> = ({
  item,
  width = 500,
  onCompletedProjectChecklistItem,
  onSaveProjectChecklistItem,
  onDeleteProjectChecklistItem,
  attributes,
  listeners,
  selectedItemId,
  setSelectedItemId,
  canEdit,
  isDragActive = false,
}) => {
  const MAX_TASK_NAME_LENGTH = 255;
  const MIN_INPUT_HEIGHT = 16;
  const MAX_INPUT_HEIGHT = 64;

  const inputRef = useRef<HTMLDivElement | null>(null);

  const [itemName, setItemName] = useState(item?.name ?? '');
  const [hovered, setHovered] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  useEffect(() => {
    item?.name && setItemName(item?.name);
  }, [item]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    let text = e.currentTarget.innerText || '';

    if (text.length > MAX_TASK_NAME_LENGTH) {
      text = text.slice(0, MAX_TASK_NAME_LENGTH);
      e.currentTarget.innerText = text;
    }
    setItemName(text);
  }, []);

  const containerRef = useOutsideClickAndEscape<HTMLDivElement>(
    showInput,
    () => {
      if (itemName !== '') {
        setShowDiscardModal(true);
        return;
      }
      setShowInput(false);
    },
    () => {
      setItemName(item?.name ?? '');
      setShowInput(false);
    }
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        const nextValue = itemName.trim();
        const prevValue = item?.name?.trim() ?? '';

        if (nextValue && nextValue !== prevValue) {
          onSaveProjectChecklistItem(nextValue, item);
        }

        setShowInput(false);
        return;
      }

      const isTextKey =
        e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (isTextKey && itemName.length >= MAX_TASK_NAME_LENGTH) {
        e.preventDefault();
      }
    },
    [itemName, item, onSaveProjectChecklistItem]
  );
  const textTaskNameRef = useRef<HTMLDivElement>(null);
  const [isTaskNameTruncated, setIsTaskNameTruncated] = useState(false);

  useEffect(() => {
    if (isMultiLine(item?.name ?? '')) {
      setIsTaskNameTruncated(true);
    } else {
      const el = textTaskNameRef.current;
      if (el) setIsTaskNameTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [item]);
  // ⭐ NEW — Set the initial value into contentEditable when editing starts
  useEffect(() => {
    if (!showInput || !inputRef.current) return;

    const el = inputRef.current;
    el.innerText = itemName;

    // Move caret to end
    const timer = setTimeout(() => {
      const sel = globalThis.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      el.focus();
    }, 10);

    return () => clearTimeout(timer);
  }, [showInput]);

  // ⭐ NEW — When input loses focus, close editor
  const handleBlur = () => {
    if (!itemName.trim()) {
      setShowInput(false);
    }
  };

  const { t } = useTranslation();

  const nameIsMultiLine = isMultiLine(item?.name ?? '');

  const getBackgroundColor = (dragActive: boolean, isHovered: boolean) => {
    if (dragActive) return theme.colors.greenSecondaryMild;
    if (isHovered) return theme.colors.grey01;
    return theme.colors.white;
  };

  if (!showInput)
    return (
      <TouchableOpacity
        disabled={!canEdit}
        accessibilityLabel={`Checklist Item | ${item.name}`}
        onLongPress={() => {
          !item.completedAt && setSelectedItemId?.(item.id);
        }}
        onPress={() => {
          setHovered(false);
          setShowInput(true);
        }}>
        <>
          {selectedItemId === item.id && !item.completedAt && (
            <>
              <Box
                height={52}
                width={width}
                borderWidth={1}
                borderColor='grey02'
                borderRadius='xs'
                backgroundColor='grey02'
                style={{ position: 'absolute', top: 6 }}
              />
              <Box
                flexDirection='row'
                height={10}
                style={{ position: 'absolute', top: 58 }}>
                <Box
                  width={10}
                  height={10}
                  borderRadius='xs'
                  backgroundColor='greenSecondary'
                  style={{ position: 'absolute', left: 0, top: -4 }}
                />
                <Box
                  height={2}
                  width={width}
                  backgroundColor='greenSecondary'
                />
              </Box>
            </>
          )}
          <Box
            accessibilityLabel='Checklist Item'
            height={52}
            width={width}
            flexDirection='row'
            alignItems='center'
            borderWidth={1}
            borderColor='grey02'
            borderRadius='xs'
            pr='s'
            style={{
              backgroundColor: getBackgroundColor(isDragActive, hovered),
            }}
            {...attributes}
            {...listeners}
            onMouseEnter={() => canEdit && setHovered(true)}
            onMouseLeave={() => canEdit && setHovered(false)}>
            <Box
              width={20}
              alignItems='center'
              justifyContent='center'
              style={{ cursor: 'grab' }}>
              {hovered && !item.completedAt && (
                <Icon name='Move1' variant='m' />
              )}
            </Box>

            {canEdit ? (
              <EnhancedAnimatedCheckbox
                isHighlightColorGreen={false}
                isHovered={undefined}
                isChecked={item.completedAt !== null}
                // This line of code affects drag-and-drop sorting.
                // onHoverChange={setHovered}
                onValueChange={() => {
                  if (item) {
                    onCompletedProjectChecklistItem(item, () => {
                      setHovered(false);
                    });
                  }
                }}
              />
            ) : (
              <Icon
                name={
                  item.completedAt === null ? 'Rectangle' : 'CheckBoxSelected'
                }
              />
            )}

            {canEdit || nameIsMultiLine ? (
              <TooltipWebV3
                marginOffset={0}
                disableHoverListener={!isTaskNameTruncated}
                disableFocusListener={!isTaskNameTruncated}
                disableTouchListener={!isTaskNameTruncated || !canEdit}
                title={
                  <Text
                    numberOfLines={0}
                    maxWidth={300}
                    flexShrink={1}
                    variant='labelSmall'
                    color='white'>
                    {item.name}
                  </Text>
                }
                placement='bottom-start'>
                <Box flex={1} alignItems='center'>
                  <Text
                    ref={textTaskNameRef}
                    ml='s'
                    mr='s'
                    flex={1}
                    flexShrink={1}
                    width={width - 116}
                    numberOfLines={1}
                    ellipsizeMode='tail'
                    variant='webBodySecondary'
                    textDecorationLine={
                      item.completedAt === null ? 'none' : 'line-through'
                    }
                    color='textPrimary'
                    style={{ cursor: 'pointer' }}>
                    {nameIsMultiLine
                      ? getFirstLine(item.name) + ' ...'
                      : item.name}
                  </Text>
                </Box>
              </TooltipWebV3>
            ) : (
              <Text
                ref={textTaskNameRef}
                ml='s'
                mr='s'
                flex={1}
                flexShrink={1}
                width={width - 116}
                numberOfLines={1}
                ellipsizeMode='tail'
                variant='webBodySecondary'
                textDecorationLine={
                  item.completedAt === null ? 'none' : 'line-through'
                }
                color='textPrimary'>
                {item.name}
              </Text>
            )}

            <Box
              width={24}
              height={24}
              ml='s'
              mr='m'
              alignItems='center'
              justifyContent='center'>
              {hovered && canEdit && (
                <Icon
                  name='Close'
                  variant='s'
                  color='grey05'
                  onPress={() => onDeleteProjectChecklistItem(item?.id)}
                />
              )}
            </Box>
          </Box>
        </>
      </TouchableOpacity>
    );

  // EDIT MODE
  return (
    <>
      {showDiscardModal && (
        <ProjectChecklistCenterModal
          contentNode={
            <Box m='l'>
              <Box flexDirection='row' marginBottom='l' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('shared:discardChanges') + '?'}
                </Text>
                <Icon
                  name='X'
                  variant='m'
                  onPress={() => {
                    setShowDiscardModal(false);
                  }}
                />
              </Box>

              <Text
                marginBottom='l'
                variant='webBodySecondary'
                color='textPrimary'
                style={{ alignSelf: 'flex-start' }}>
                You have unsaved changes. Are you sure you want to discard them?
              </Text>

              <Box style={{ alignSelf: 'flex-end' }} width={125}>
                <Button
                  height={48}
                  fullWidth={true}
                  variant='primary'
                  onPress={() => {
                    setShowDiscardModal(false);
                    setItemName('');
                    setShowInput(false);
                  }}
                  accessibilityLabel={t('shared:discard')}>
                  <Text variant='webSmall' color='white'>
                    {t('shared:discard')}
                  </Text>
                </Button>
              </Box>
            </Box>
          }
          onClose={() => {
            setShowDiscardModal(false);
          }}
          visible={showDiscardModal}
        />
      )}
      <div ref={containerRef}>
        <Box
          p='m'
          minHeight={98}
          width={width}
          mb='m'
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          flexDirection='row'>
          <Box>
            <Box flexDirection='row' alignItems='center'>
              <Box
                flexDirection='row'
                width={width - 68}
                borderRadius='xs'
                borderWidth={1}
                borderColor='black'
                px='m'
                py='s'>
                <div
                  ref={inputRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur} // ⭐ CLOSE ON BLUR
                  style={{
                    width: width - 100,
                    outline: 'none',
                    cursor: 'text',
                    minHeight: MIN_INPUT_HEIGHT,
                    maxHeight: MAX_INPUT_HEIGHT,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    display: 'flex',
                    alignItems: isMultiLine(itemName) ? 'flex-start' : 'center',
                    fontSize: theme.textVariants.webBodySecondary.fontSize,
                    fontFamily: theme.textVariants.webBodySecondary.fontFamily,
                    lineHeight:
                      theme.textVariants.webBodySecondary.lineHeight + 'px',
                    color: theme.colors.textSecondary,
                  }}
                  data-placeholder='Item name...'
                />
                <Text
                  ml='s'
                  variant='bodySecondary'
                  color='grey04'
                  alignSelf='flex-end'>
                  {255 - itemName.length}
                </Text>
              </Box>

              <Box
                width={16}
                height={16}
                ml='s'
                alignItems='center'
                justifyContent='center'>
                <Icon
                  name='Close'
                  variant='s'
                  color='grey05'
                  onPress={() => {
                    if (item) {
                      onDeleteProjectChecklistItem(item.id);
                    } else {
                      setItemName('');
                    }
                    setShowInput(false);
                  }}
                />
              </Box>
            </Box>

            <Text
              ml='m'
              variant='webBodyHintSubline'
              color='grey04'
              style={{ marginTop: 10 }}>
              {t('models:projects.checklist.pressShiftEnterForNewLine')}
            </Text>
          </Box>
        </Box>
      </div>
    </>
  );
};
